import React, { useEffect, useState, useCallback } from 'react';
import './App.css';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import {
  getStoredToken,
  clearStoredToken,
  adminLogin as apiAdminLogin,
  fetchAdminMarkets,
  fetchAdminMarket,
  createMarket as apiCreateMarket,
  updateMarket as apiUpdateMarket,
  deleteMarket as apiDeleteMarket,
  type MarketStatus,
  fetchAdminUsers,
  fetchAdminBots,
  createBot as apiCreateBot,
  updateBot as apiUpdateBot,
  deleteBot as apiDeleteBot,
  autoCreateBots as apiAutoCreateBots,
  fetchAdminOrders,
  fetchAdminPositions,
  fetchResolutionLogs,
  fetchUserLimits,
  fetchTransactions,
  createOrder as apiCreateOrder,
  cancelOrder as apiCancelOrder,
  deleteOrder as apiDeleteOrder,
  fetchAdminNotifications,
  createNotification as apiCreateNotification,
  updateNotification as apiUpdateNotification,
  deleteNotification as apiDeleteNotification,
  fetchOrderCategories,
  createOrderCategory as apiCreateOrderCategory,
  updateOrderCategoryApi,
  deleteOrderCategory as apiDeleteOrderCategory,
  resolveMarket as apiResolveMarket,
  moderateMarket as apiModerateMarket,
  withdrawMarketWalletToDeposit as apiWithdrawMarketWalletToDeposit,
  updateUserRevshare as apiUpdateUserRevshare,
  updateUserEmail as apiUpdateUserEmail,
  recordWithdrawal as apiRecordWithdrawal,
  fetchAdminUsdtWithdrawals,
  approveUsdtWithdrawal as apiApproveUsdtWithdrawal,
  rejectUsdtWithdrawal as apiRejectUsdtWithdrawal,
  fetchContentCategories,
  fetchContentItems,
  createContentCategory as apiCreateContentCategory,
  updateContentCategory as apiUpdateContentCategory,
  deleteContentCategory as apiDeleteContentCategory,
  createContentItem as apiCreateContentItem,
  updateContentItem as apiUpdateContentItem,
  deleteContentItem as apiDeleteContentItem,
  getMe,
} from './api';

type AdminTab = 'markets' | 'moderation' | 'resolve' | 'users' | 'bots' | 'orders' | 'positions' | 'logs' | 'withdrawals' | 'content' | 'notifications' | 'orderCategories';
type AdminRole = 'admin' | 'resolver' | 'moderator' | 'user' | string;

type AdminUserLike = {
  id?: number;
  username?: string | null;
  firstName?: string | null;
  telegramId?: string | null;
  adminEmail?: string | null;
} | null | undefined;

/** Telegram username / имя / email / id для таблиц админки */
function displayAdminUsername(user: AdminUserLike, fallbackUserId?: number | null): string {
  if (user) {
    const u = user.username?.trim();
    if (u) return u;
    const fn = user.firstName?.trim();
    if (fn) return fn;
    const em = user.adminEmail?.trim();
    if (em) return em;
    const tg = user.telegramId != null ? String(user.telegramId).trim() : '';
    if (tg) return tg;
    if (user.id != null) return `#${user.id}`;
  }
  if (fallbackUserId != null && fallbackUserId !== undefined) return `#${fallbackUserId}`;
  return '—';
}

function hasValidToken(): boolean {
  const t = getStoredToken();
  return typeof t === 'string' && t.trim().length > 0;
}

const App: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [authChecked, setAuthChecked] = useState<boolean>(() => !hasValidToken());
  const [tab, setTab] = useState<AdminTab>('markets');
  const [currentRole, setCurrentRole] = useState<AdminRole | null>(null);

  const goToLogin = useCallback(() => {
    clearStoredToken();
    setToken(null);
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    window.addEventListener('admin-logout', goToLogin);
    return () => window.removeEventListener('admin-logout', goToLogin);
  }, [goToLogin]);

  useEffect(() => {
    if (!token || !token.trim()) {
      setAuthChecked(true);
      if (token !== null) setToken(null);
      return;
    }
    let cancelled = false;
    setAuthChecked(false);
    const timeout = window.setTimeout(() => {
      if (!cancelled) {
        clearStoredToken();
        setToken(null);
        setAuthChecked(true);
      }
    }, 10000);
    getMe()
      .then((me) => {
        if (!cancelled) setCurrentRole(me.role);
        if (!cancelled) setAuthChecked(true);
      })
      .catch(() => {
        if (!cancelled) {
          clearStoredToken();
          setToken(null);
          setCurrentRole(null);
          setAuthChecked(true);
        }
      })
      .finally(() => {
        window.clearTimeout(timeout);
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [token]);

  const handleLogin = useCallback((t: string) => {
    setToken(t);
  }, []);

  const handleLogout = useCallback(() => {
    clearStoredToken();
    setToken(null);
    setCurrentRole(null);
  }, []);

  const labels: Record<AdminTab, string> = {
    markets: 'Markets',
    moderation: 'Moderation',
    resolve: 'Resolve',
    users: 'Users',
    bots: 'Bots',
    orders: 'Orders',
    positions: 'Positions',
    logs: 'Logs',
    withdrawals: 'Withdrawals',
    content: 'Content (FAQ/Tutorials)',
    notifications: 'Notifications',
    orderCategories: 'Order categories',
  };
  const visibleTabs: AdminTab[] = currentRole === 'moderator'
    ? ['markets', 'content', 'notifications']
    : (Object.keys(labels) as AdminTab[]);

  useEffect(() => {
    if (!visibleTabs.includes(tab)) {
      setTab(visibleTabs[0]);
    }
  }, [tab, visibleTabs]);

  if (!token || !token.trim()) {
    return <LoginScreen onLogin={handleLogin} />;
  }
  if (!authChecked) {
    return (
      <div className="LoginScreen">
        <h1>Eventum Admin</h1>
        <p>Checking session...</p>
      </div>
    );
  }

  return (
    <div className="AdminRoot">
      <aside className="AdminSidebar">
        <h1 className="AdminLogo">Eventum Admin</h1>
        <button type="button" className="AdminLogout" onClick={handleLogout}>
          Log out
        </button>
        <nav className="AdminNav">
          {visibleTabs.map((t) => (
            <button
              key={t}
              type="button"
              className={tab === t ? 'AdminNavItem active' : 'AdminNavItem'}
              onClick={() => setTab(t)}
            >
              {labels[t]}
            </button>
          ))}
        </nav>
      </aside>
      <main className="AdminMain">
        <AdminPanel tab={tab} role={currentRole} />
      </main>
    </div>
  );
};

const LoginScreen: React.FC<{ onLogin: (token: string) => void }> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Enter email');
      return;
    }
    if (!password) {
      setError('Enter password');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { accessToken } = await apiAdminLogin(email.trim(), password);
      onLogin(accessToken);
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? (e instanceof Error ? e.message : 'Login error');
      setError(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="LoginScreen">
      <h1>Eventum Admin</h1>
      <form onSubmit={doLogin} className="LoginForm">
        <label>
          Emai
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            autoComplete="email"
            className="LoginInput"
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            className="LoginInput"
          />
        </label>
        {error && <p className="LoginError">{error}</p>}
        <button type="submit" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

const AdminPanel: React.FC<{ tab: AdminTab; role: AdminRole | null }> = ({ tab, role }) => {
  const isModerator = role === 'moderator';
  return (
    <>
      {tab === 'markets' && <MarketsSection readOnly={isModerator} />}
      {tab === 'moderation' && <ModerationSection />}
      {tab === 'resolve' && <ResolveSection />}
      {tab === 'users' && <UsersSection />}
      {tab === 'bots' && <BotsSection />}
      {tab === 'orders' && <OrdersSection />}
      {tab === 'positions' && <PositionsSection />}
      {tab === 'logs' && <LogsSection />}
      {tab === 'withdrawals' && <WithdrawalsSection />}
      {tab === 'content' && <ContentSection />}
      {tab === 'notifications' && <NotificationsSection />}
      {tab === 'orderCategories' && <OrderCategoriesSection />}
    </>
  );
};

const MARKET_STATUS_LIST: MarketStatus[] = [
  'OPEN',
  'RESOLVED',
  'VOID',
  'CLOSED_FOR_TRADING',
  'PENDING_MODERATION',
  'REJECTED',
];

const MarketsSection: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [formMode, setFormMode] = useState<'create' | number | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [editModerationComment, setEditModerationComment] = useState<string | null>(null);
  const [form, setForm] = useState({
    question: '',
    description: '',
    category: '',
    imageUrl: '',
    deadline: '',
    status: 'PENDING_MODERATION' as MarketStatus,
    sourceLinksText: '',
    initialYesPercent: '50',
    targetYesPercent: '50',
    targetNoPercent: '50',
    botEnabled: false,
    botMinOrderAmount: '1',
    botMaxOrderAmount: '25',
    botCooldownMs: '7000',
    botMaxExposure: '1000',
    manualVolume: '',
  });

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchAdminMarkets({ page, limit: 20, search: search || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load markets'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (formMode === 'create') {
      setEditModerationComment(null);
      setForm({
        question: '',
        description: '',
        category: '',
        imageUrl: '',
        deadline: new Date().toISOString().slice(0, 16),
        status: 'PENDING_MODERATION',
        sourceLinksText: '',
        initialYesPercent: '50',
        targetYesPercent: '50',
        targetNoPercent: '50',
        botEnabled: false,
        botMinOrderAmount: '1',
        botMaxOrderAmount: '25',
        botCooldownMs: '7000',
        botMaxExposure: '1000',
        manualVolume: '',
      });
      setFormError(null);
    } else if (typeof formMode === 'number') {
      setFormError(null);
      fetchAdminMarket(formMode)
        .then((m: any) => {
          setEditModerationComment(
            m.status === 'REJECTED' ? (m.moderationComment ?? null) : null,
          );
          setForm({
            question: m.question ?? '',
            description: m.description ?? '',
            category: m.category ?? '',
            imageUrl: m.imageUrl ?? '',
            deadline: m.deadline ? new Date(m.deadline).toISOString().slice(0, 16) : '',
            status: (m.status ?? 'PENDING_MODERATION') as MarketStatus,
            sourceLinksText: Array.isArray(m.sourceLinks) ? m.sourceLinks.join('\n') : '',
            initialYesPercent: String(m.initialYesPercent ?? '50'),
            targetYesPercent: String(m.targetYesPercent ?? '50'),
            targetNoPercent: String(m.targetNoPercent ?? '50'),
            botEnabled: Boolean(m.botEnabled),
            botMinOrderAmount: String(m.botMinOrderAmount ?? '1'),
            botMaxOrderAmount: String(m.botMaxOrderAmount ?? '25'),
            botCooldownMs: String(m.botCooldownMs ?? '7000'),
            botMaxExposure: String(m.botMaxExposure ?? '1000'),
            manualVolume: String(m.manualVolume ?? ''),
          });
        })
        .catch(() => setFormError('Failed to load market'));
    }
  }, [formMode]);

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSaving(true);
    try {
      const sourceLinks = form.sourceLinksText.trim()
        ? form.sourceLinksText.trim().split(/\n/).map((s) => s.trim()).filter(Boolean)
        : null;
      const deadline = form.deadline ? new Date(form.deadline).toISOString() : new Date().toISOString();
      const initialYesPercent = Number(form.initialYesPercent);
      const targetYesPercent = Number(form.targetYesPercent);
      const targetNoPercent = Number(form.targetNoPercent);
      const botCooldownMs = Number(form.botCooldownMs);
      const manualVolume = form.manualVolume.trim()
        ? form.manualVolume.trim()
        : null;
      if (formMode === 'create') {
        await apiCreateMarket({
          question: form.question,
          description: form.description || null,
          category: form.category || null,
          imageUrl: form.imageUrl || null,
          deadline,
          status: form.status,
          sourceLinks,
          initialYesPercent,
          targetYesPercent,
          targetNoPercent,
          botEnabled: form.botEnabled,
          botMinOrderAmount: form.botMinOrderAmount || '1',
          botMaxOrderAmount: form.botMaxOrderAmount || '25',
          botCooldownMs,
          botMaxExposure: form.botMaxExposure || '1000',
          manualVolume,
        });
      } else if (typeof formMode === 'number') {
        await apiUpdateMarket(formMode, {
          question: form.question,
          description: form.description || null,
          category: form.category || null,
          imageUrl: form.imageUrl || null,
          deadline,
          status: form.status,
          sourceLinks,
          initialYesPercent,
          targetYesPercent,
          targetNoPercent,
          botEnabled: form.botEnabled,
          botMinOrderAmount: form.botMinOrderAmount || '1',
          botMaxOrderAmount: form.botMaxOrderAmount || '25',
          botCooldownMs,
          botMaxExposure: form.botMaxExposure || '1000',
          manualVolume,
        });
      }
      setFormMode(null);
      load();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Error');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiDeleteMarket(id);
      setDeleteConfirmId(null);
      load();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Delete failed');
    }
  };

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const markets = result?.data ?? [];

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;
  return (
    <section>
      <h2>Markets</h2>
      <p>
        {readOnly
          ? 'Events, YES/NO options, deadline, status. Read only.'
          : 'Events, YES/NO options, deadline, status. Create, edit, delete.'}
      </p>
      <div className="AdminToolbar">
        <input type="text" placeholder="Search by question or id" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)} className="LoginInput" style={{ width: 220, marginRight: 8 }} />
        <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
        {!readOnly && (
          <button type="button" style={{ marginLeft: 12 }} onClick={() => setFormMode(formMode === 'create' ? null : 'create')}>
            {formMode === 'create' ? 'Cancel' : 'Create market'}
          </button>
        )}
      </div>

      {!readOnly && formMode !== null && (
        <form onSubmit={submitForm} className="AdminForm">
          <h3>{formMode === 'create' ? 'New market' : `Edit market #${formMode}`}</h3>
          <label>Question * <input type="text" value={form.question} onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))} required className="LoginInput" /></label>
          <label>
            Description
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="LoginInput"
              rows={4}
              placeholder="Resolution criteria: only MAJOR version upgrade counts. Example: iOS 18 -> iOS 19 is YES; iOS 18 -> iOS 18.1 is NOT counted."
            />
          </label>
          <p className="AdminFormHint">
            Tip: always define what counts and what does not count for resolution.
          </p>
          <label>Category <input type="text" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="LoginInput" /></label>
          <label>Image URL <input type="url" value={form.imageUrl} onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))} className="LoginInput" placeholder="https://example.com/image.jpg" /></label>
          <label>Deadline * <input type="datetime-local" value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))} required className="LoginInput" /></label>
          <label>Status <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as MarketStatus }))} className="LoginInput">
            {MARKET_STATUS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
          {form.status === 'REJECTED' && (
            <p className="AdminFormHint" style={{ marginTop: 0 }}>
              Rejection reason (from moderation):{' '}
              {editModerationComment?.trim()
                ? editModerationComment
                : '— (no comment was saved)'}
            </p>
          )}
          <label>Source links (one per line) <textarea value={form.sourceLinksText} onChange={(e) => setForm((f) => ({ ...f, sourceLinksText: e.target.value }))} className="LoginInput" rows={2} placeholder="https://..." /></label>
          <label>Preset volume (optional) <input type="text" value={form.manualVolume} onChange={(e) => setForm((f) => ({ ...f, manualVolume: e.target.value }))} className="LoginInput" placeholder="e.g. 1500" /></label>
          <label>Initial coefficient — YES % (1–99) <input type="number" min={1} max={99} step={0.01} value={form.initialYesPercent} onChange={(e) => setForm((f) => ({ ...f, initialYesPercent: e.target.value }))} className="LoginInput" placeholder="50" /></label>
          <hr className="AdminFormDivider" />
          <h4 className="AdminFormSectionTitle">Bot settings</h4>
          <label className="AdminFormCheckboxRow">
            <input type="checkbox" checked={form.botEnabled} onChange={(e) => setForm((f) => ({ ...f, botEnabled: e.target.checked }))} />
            <span>Enable bot observer for this market</span>
          </label>
          <div className="AdminFormBotGrid">
            <label>Target YES % <input type="number" min={0} max={100} step={0.01} value={form.targetYesPercent} onChange={(e) => setForm((f) => ({ ...f, targetYesPercent: e.target.value }))} className="LoginInput" /></label>
            <label>Target NO % <input type="number" min={0} max={100} step={0.01} value={form.targetNoPercent} onChange={(e) => setForm((f) => ({ ...f, targetNoPercent: e.target.value }))} className="LoginInput" /></label>
            <label>Bot min order amount <input type="text" value={form.botMinOrderAmount} onChange={(e) => setForm((f) => ({ ...f, botMinOrderAmount: e.target.value }))} className="LoginInput" /></label>
            <label>Bot max order amount <input type="text" value={form.botMaxOrderAmount} onChange={(e) => setForm((f) => ({ ...f, botMaxOrderAmount: e.target.value }))} className="LoginInput" /></label>
            <label>Bot cooldown (ms) <input type="number" min={0} step={1} value={form.botCooldownMs} onChange={(e) => setForm((f) => ({ ...f, botCooldownMs: e.target.value }))} className="LoginInput" /></label>
            <label>Bot max exposure <input type="text" value={form.botMaxExposure} onChange={(e) => setForm((f) => ({ ...f, botMaxExposure: e.target.value }))} className="LoginInput" /></label>
          </div>
          {formError && <p className="AdminError">{formError}</p>}
          <div className="AdminFormActions">
            <button type="submit" disabled={formSaving}>{formSaving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={() => setFormMode(null)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="AdminTableWrap">
        <table className="AdminTable">
          <thead>
            <tr>
              <th>#</th>
              <th>Question</th>
              <th>Deadline</th>
              <th>Status</th>
              <th>Reject reason</th>
              <th>Resolution</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {markets.map((m) => (
              <tr key={m.id}>
                <td>{m.id}</td>
                <td>{m.question}</td>
                <td>{m.deadline ? new Date(m.deadline).toLocaleString() : '—'}</td>
                <td>{m.status}</td>
                <td
                  title={m.status === 'REJECTED' && m.moderationComment ? m.moderationComment : undefined}
                  style={{
                    maxWidth: 280,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: 13,
                    color: m.status === 'REJECTED' && m.moderationComment ? undefined : '#888',
                  }}
                >
                  {m.status === 'REJECTED'
                    ? (m.moderationComment?.trim() || '—')
                    : '—'}
                </td>
                <td>{m.resolution ?? '—'}</td>
                <td>
                  {readOnly ? (
                    <span>—</span>
                  ) : (
                    <>
                      <button type="button" onClick={() => setFormMode(m.id)}>Edit</button>
                      {' '}
                      {deleteConfirmId === m.id ? (
                        <>
                          <span>Delete? </span>
                          <button type="button" className="AdminBtnReject" onClick={() => handleDelete(m.id)}>Yes</button>
                          <button type="button" onClick={() => setDeleteConfirmId(null)}>No</button>
                        </>
                      ) : (
                        <button type="button" className="AdminBtnReject" onClick={() => setDeleteConfirmId(m.id)}>Delete</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const ModerationSection: React.FC = () => {
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [acting, setActing] = useState<number | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectingId, setRejectingId] = useState<number | null>(null);

  const load = useCallback((p: number, s: string) => {
    setLoading(true);
    setError(null);
    fetchAdminMarkets({ page: p, limit: 50, search: s || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, search);
  }, [page, search, load]);

  const handleModerate = async (marketId: number, approved: boolean, comment?: string) => {
    setActing(marketId);
    try {
      await apiModerateMarket(marketId, { approved, comment });
      setRejectingId(null);
      setRejectComment('');
      load(page, search);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Error');
    } finally {
      setActing(null);
    }
  };

  const markets = (result?.data ?? []).filter((m: any) => m.status === 'PENDING_MODERATION');
  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;
  return (
    <section>
      <h2>Moderation</h2>
      <p>Markets with status Pending moderation. Approve to make live; reject to set Rejected.</p>
      <div className="AdminToolbar">
        <input type="text" placeholder="Search by question or id" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setSearch(searchInput), setPage(1))} className="LoginInput" style={{ width: 220, marginRight: 8 }} />
        <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
      </div>
      {markets.length === 0 ? (
        <p>No markets pending moderation on this page.</p>
      ) : (
        <div className="AdminTableWrap">
          <table className="AdminTable">
            <thead>
              <tr>
                <th>#</th>
                <th>Question</th>
                <th>Deadline</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {markets.map((m) => (
                <tr key={m.id}>
                  <td>{m.id}</td>
                  <td>{m.question}</td>
                  <td>{m.deadline ? new Date(m.deadline).toLocaleString() : '—'}</td>
                  <td>
                    {rejectingId === m.id ? (
                      <span className="ModerationRejectRow">
                        <input type="text" placeholder="Rejection reason (optional)" value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} className="LoginInput" style={{ width: 220, marginRight: 8 }} />
                        <button type="button" onClick={() => handleModerate(m.id, false, rejectComment)} disabled={!!acting}>Confirm reject</button>
                        <button type="button" onClick={() => { setRejectingId(null); setRejectComment(''); }}>Cancel</button>
                      </span>
                    ) : (
                      <span>
                        <button type="button" onClick={() => handleModerate(m.id, true)} disabled={!!acting} className="AdminBtnApprove">Approve</button>
                        <button type="button" onClick={() => setRejectingId(m.id)} disabled={!!acting} className="AdminBtnReject">Reject</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} markets)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const ResolveSection: React.FC = () => {
  const [markets, setMarkets] = useState<any[]>([]);
  const [withdrawMarkets, setWithdrawMarkets] = useState<any[]>([]);
  const [marketId, setMarketId] = useState('');
  const [currentUser, setCurrentUser] = useState<{ id: number; role: string } | null>(null);
  const [resolution, setResolution] = useState<'YES' | 'NO' | 'VOID'>('YES');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [withdrawMarketId, setWithdrawMarketId] = useState<string>('');
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [withdrawDestinationAddress, setWithdrawDestinationAddress] = useState<string>('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminMarkets({ limit: 200 })
      .then((res: any) => setMarkets(Array.isArray(res?.data) ? res.data.filter((m: any) => m.status === 'OPEN' || m.status === 'CLOSED_FOR_TRADING') : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getMe()
      .then((me) => setCurrentUser({ id: me.id, role: me.role }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchAdminMarkets({ limit: 200 })
      .then((res: any) =>
        setWithdrawMarkets(
          Array.isArray(res?.data)
            ? res.data.filter((m: any) => m.settlementStage === 'WINNERS_PAID')
            : [],
        ),
      )
      .catch(() => {});
  }, []);

  const selectedWithdrawMarket = withdrawMarkets.find((m) => String(m.id) === withdrawMarketId) || null;

  const withdrawFromMarket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawMarketId) {
      setWithdrawError('Select market');
      return;
    }
    const normalizedWithdrawAmount = withdrawAmount.trim().toUpperCase();
    if (!normalizedWithdrawAmount) {
      setWithdrawError('Enter amount or use MAX');
      return;
    }

    let amountToSend: string;
    if (normalizedWithdrawAmount === 'MAX') {
      amountToSend = 'MAX';
    } else {
      const amountNum = Number(withdrawAmount);
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        setWithdrawError('Enter a positive amount or "MAX"');
        return;
      }
      amountToSend = amountNum.toFixed(8);
    }
    if (!Number.isFinite(Number(withdrawMarketId))) {
      setWithdrawError('Invalid market id');
      return;
    }

    setWithdrawError(null);
    setWithdrawSuccess(null);
    setWithdrawLoading(true);
    try {
      await apiWithdrawMarketWalletToDeposit(Number(withdrawMarketId), {
        amount: amountToSend,
        destinationAddress: withdrawDestinationAddress.trim() || undefined,
      });
      setWithdrawSuccess('Withdraw request created. It will be processed automatically.');
      setWithdrawAmount('');
      setWithdrawDestinationAddress('');
    } catch (e: any) {
      setWithdrawError(e?.response?.data?.message || e?.message || 'Error');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!marketId) {
      setError('Select market');
      return;
    }
    if (!currentUser) {
      setError('User not loaded');
      return;
    }
    setError(null);
    setSuccess(false);
    setLoading(true);
    try {
      await apiResolveMarket(Number(marketId), {
        resolverUserId: currentUser.id,
        resolution,
        comment: comment.trim() || undefined,
      });
      setSuccess(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <h2>Resolve market</h2>
      <p>Admin/oracle sets outcome: YES / NO / VOID. Payouts are calculated automatically after save.</p>
      <form onSubmit={onSubmit} className="ResolveForm">
        <label>Market (id): <select value={marketId} onChange={(e) => setMarketId(e.target.value)}>
          <option value="">—</option>
          {markets.map((m) => (
            <option key={m.id} value={m.id}>#{m.id} {m.question}</option>
          ))}
        </select></label>
        <label>Resolver: <span className="ResolverInfo">{currentUser ? `Текущий пользователь (#${currentUser.id})` : '…'}</span></label>
        <label>Result: <select value={resolution} onChange={(e) => setResolution(e.target.value as 'YES' | 'NO' | 'VOID')}>
          <option value="YES">YES</option>
          <option value="NO">NO</option>
          <option value="VOID">VOID</option>
        </select></label>
        <label>Comment: <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} /></label>
        {error && <p className="AdminError">{error}</p>}
        {success && <p className="AdminSuccess">Resolution saved. Payouts applied.</p>}
        <button type="submit" disabled={loading || !currentUser}>{loading ? 'Saving...' : 'Save resolution'}</button>
      </form>

      <hr className="AdminFormDivider" />

      <h3>Withdraw market wallet to TON deposit</h3>
      <p>
        After the market is fully settled (<code>WINNERS_PAID</code>), you can withdraw funds from <code>market.wallet</code> to <code>TON_DEPOSIT_ADDRESS</code>.
      </p>

      <form onSubmit={withdrawFromMarket} className="ResolveForm">
        <label>
          Market (id)
          <select
            value={withdrawMarketId}
            onChange={(e) => setWithdrawMarketId(e.target.value)}
          >
            <option value="">—</option>
            {withdrawMarkets.map((m) => (
              <option key={m.id} value={m.id}>
                #{m.id} {m.question}
              </option>
            ))}
          </select>
        </label>

        {selectedWithdrawMarket?.wallet?.address && (
          <p style={{ margin: 0, color: '#888', fontSize: 13 }}>
            Market wallet: {String(selectedWithdrawMarket.wallet.address).slice(0, 8)}...
          </p>
        )}

        <label>
          Amount (TON)
          <input
            type="text"
            placeholder="e.g. 1.5 or MAX"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
          />
        </label>

        <label>
          Destination TON address (optional)
          <input
            type="text"
            placeholder="Leave empty to use TON_DEPOSIT_ADDRESS"
            value={withdrawDestinationAddress}
            onChange={(e) => setWithdrawDestinationAddress(e.target.value)}
          />
        </label>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setWithdrawAmount('MAX')}
            disabled={withdrawLoading || !currentUser}
          >
            Max
          </button>
        </div>

        {withdrawError && <p className="AdminError">{withdrawError}</p>}
        {withdrawSuccess && <p className="AdminSuccess">{withdrawSuccess}</p>}

        <button type="submit" disabled={withdrawLoading || !currentUser}>
          {withdrawLoading ? 'Creating...' : 'Withdraw to deposit'}
        </button>
      </form>
    </section>
  );
};

const UsersSection: React.FC = () => {
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [revshareEdit, setRevshareEdit] = useState<Record<number, string>>({});
  const [emailEdit, setEmailEdit] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savingEmailId, setSavingEmailId] = useState<number | null>(null);

  const load = useCallback((p: number, s: string) => {
    setLoading(true);
    setError(null);
    fetchAdminUsers({ page: p, limit: 20, search: s || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, search);
  }, [page, search, load]);

  const saveRevshare = async (userId: number) => {
    const raw = revshareEdit[userId];
    const value = raw === '' || raw == null ? null : parseFloat(raw);
    if (value !== null && (isNaN(value) || value < 0 || value > 100)) return;
    setSavingId(userId);
    try {
      await apiUpdateUserRevshare(userId, value);
      setRevshareEdit((prev) => { const n = { ...prev }; delete n[userId]; return n; });
      load(page, search);
    } catch {
      setError('Failed to save revshare');
    } finally {
      setSavingId(null);
    }
  };

  const saveEmail = async (userId: number) => {
    const value = emailEdit[userId]?.trim() ?? '';
    setSavingEmailId(userId);
    try {
      await apiUpdateUserEmail(userId, value || null);
      setEmailEdit((prev) => { const n = { ...prev }; delete n[userId]; return n; });
      load(page, search);
    } catch {
      setError('Failed to save email');
    } finally {
      setSavingEmailId(null);
    }
  };

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const users = result?.data ?? [];

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;
  return (
    <section>
      <h2>Users</h2>
      <p>telegram_id, wallet, balance, email, revshare. Revshare % — platform profit share on resolve (0–100).</p>
      <div className="AdminToolbar">
        <input type="text" placeholder="Search by email, username, telegram" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setSearch(searchInput), setPage(1))} className="LoginInput" style={{ width: 220, marginRight: 8 }} />
        <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
      </div>
      <div className="AdminTableWrap">
        <table className="AdminTable">
          <thead>
            <tr>
              <th>#</th>
              <th>telegram_id</th>
              <th>username</th>
              <th>email</th>
              <th>wallet</th>
              <th>Available</th>
              <th>Locked</th>
              <th>role</th>
              <th>Revshare %</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.telegramId}</td>
                <td>{u.username ?? '—'}</td>
                <td>
                  <span className="RevshareCell">
                    <input type="email" placeholder="—" value={emailEdit[u.id] ?? (u.email ?? '')} onChange={(e) => setEmailEdit((prev) => ({ ...prev, [u.id]: e.target.value }))} className="RevshareInput" style={{ minWidth: 140 }} />
                    <button type="button" onClick={() => saveEmail(u.id)} disabled={savingEmailId === u.id}>{savingEmailId === u.id ? '...' : 'OK'}</button>
                  </span>
                </td>
                <td>{u.walletAddress ?? '—'}</td>
                <td>{u.balanceAvailable}</td>
                <td>{u.balanceLocked}</td>
                <td>{u.role}</td>
                <td>
                  <span className="RevshareCell">
                    <input type="number" min={0} max={100} step={0.01} placeholder="—" value={revshareEdit[u.id] ?? (u.revsharePercent ?? '')} onChange={(e) => setRevshareEdit((prev) => ({ ...prev, [u.id]: e.target.value }))} className="RevshareInput" />
                    <button type="button" onClick={() => saveRevshare(u.id)} disabled={savingId === u.id}>{savingId === u.id ? '...' : 'OK'}</button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const BotsSection: React.FC = () => {
  const [result, setResult] = useState<{
    data: any[];
    total: number;
    page: number;
    limit: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [formMode, setFormMode] = useState<'create' | number | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const [form, setForm] = useState({
    telegramId: '',
    username: '',
    balanceAvailable: '1000',
  });

  const load = useCallback((p: number, s: string) => {
    setLoading(true);
    setError(null);
    fetchAdminBots({ page: p, limit: 20, search: s || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load bots'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, search);
  }, [page, search, load]);

  useEffect(() => {
    if (formMode === 'create') {
      setFormError(null);
      setForm({ telegramId: '', username: '', balanceAvailable: '1000' });
    } else if (typeof formMode === 'number') {
      const bot = result?.data?.find((x) => x.id === formMode);
      if (bot) {
        setFormError(null);
        setForm({
          telegramId: String(bot.telegramId ?? ''),
          username: bot.username ?? '',
          balanceAvailable: String(bot.balanceAvailable ?? '0'),
        });
      }
    }
  }, [formMode, result]);

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSaving(true);
    try {
      const telegramId = form.telegramId.trim();
      const username = form.username.trim() || null;
      const balanceAvailable = form.balanceAvailable.trim();
      if (!telegramId || !balanceAvailable) {
        setFormError('telegramId and balanceAvailable are required');
        return;
      }
      if (formMode === 'create') {
        await apiCreateBot({ telegramId, username, balanceAvailable });
      } else if (typeof formMode === 'number') {
        await apiUpdateBot(formMode, { username, balanceAvailable });
      }
      setFormMode(null);
      setDeleteConfirmId(null);
      load(1, search);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Error');
    } finally {
      setFormSaving(false);
    }
  };

  const handleAutoCreate = async () => {
    setFormError(null);
    setFormSaving(true);
    try {
      await apiAutoCreateBots({ count: 1, balanceAvailable: form.balanceAvailable || '1000' });
      setFormMode(null);
      setDeleteConfirmId(null);
      load(1, search);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Auto create failed');
    } finally {
      setFormSaving(false);
    }
  };

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const bots = result?.data ?? [];

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;

  return (
    <section>
      <h2>Bots</h2>
      <p>Users with <code>is_bot=true</code> used by the bot observer. Create/edit/deactivate (delete disabled if bot has open orders/positions).</p>
      <div className="AdminToolbar">
        <input
          type="text"
          placeholder="Search by telegram_id or username"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (setSearch(searchInput), setPage(1))}
          className="LoginInput"
          style={{ width: 260, marginRight: 8 }}
        />
        <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
        <button
          type="button"
          style={{ marginLeft: 8 }}
          onClick={handleAutoCreate}
          disabled={formSaving}
        >
          Auto-create bot
        </button>
        <button type="button" style={{ marginLeft: 8 }} onClick={() => setFormMode(formMode === 'create' ? null : 'create')}>
          {formMode === 'create' ? 'Cancel' : 'Create bot'}
        </button>
      </div>

      {formMode !== null && (
        <form onSubmit={submitForm} className="AdminForm" style={{ marginTop: 16 }}>
          <h3>{formMode === 'create' ? 'New bot' : `Edit bot #${formMode}`}</h3>

          {formMode === 'create' ? (
            <label>
              telegram_id *
              <input
                type="text"
                value={form.telegramId}
                onChange={(e) => setForm((f) => ({ ...f, telegramId: e.target.value }))}
                required
                className="LoginInput"
              />
            </label>
          ) : (
            <label>
              telegram_id
              <input className="LoginInput" type="text" value={form.telegramId} disabled />
            </label>
          )}

          <label>
            username
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              className="LoginInput"
            />
          </label>

          <label>
            balanceAvailable *
            <input
              type="text"
              value={form.balanceAvailable}
              onChange={(e) => setForm((f) => ({ ...f, balanceAvailable: e.target.value }))}
              className="LoginInput"
            />
          </label>

          {formError && <p className="AdminError">{formError}</p>}

          <div className="AdminFormActions">
            <button type="submit" disabled={formSaving}>{formSaving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={() => setFormMode(null)} style={{ marginLeft: 8 }}>Cancel</button>
          </div>
        </form>
      )}

      <div className="AdminTableWrap" style={{ marginTop: 16 }}>
        <table className="AdminTable">
          <thead>
            <tr>
              <th>#</th>
              <th>telegram_id</th>
              <th>username</th>
              <th>Available</th>
              <th>Locked</th>
              <th>role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bots.map((b) => (
              <tr key={b.id}>
                <td>{b.id}</td>
                <td>{b.telegramId}</td>
                <td>{b.username ?? '—'}</td>
                <td>{b.balanceAvailable}</td>
                <td>{b.balanceLocked}</td>
                <td>{b.role}</td>
                <td>
                  <button type="button" onClick={() => setFormMode(b.id)} disabled={formSaving}>
                    Edit
                  </button>
                  <span style={{ marginLeft: 8 }} />
                  {deleteConfirmId === b.id ? (
                    <>
                      <span style={{ marginRight: 8 }}>Delete?</span>
                      <button
                        type="button"
                        className="AdminBtnReject"
                        onClick={async () => {
                          setFormSaving(true);
                          try {
                            await apiDeleteBot(b.id);
                            setDeleteConfirmId(null);
                            setFormMode(null);
                            load(1, search);
                          } catch (err: any) {
                            setFormError(err?.response?.data?.message || err?.message || 'Delete failed');
                          } finally {
                            setFormSaving(false);
                          }
                        }}
                        disabled={formSaving}
                      >
                        Yes
                      </button>
                      <button type="button" onClick={() => setDeleteConfirmId(null)} style={{ marginLeft: 8 }} disabled={formSaving}>
                        No
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setDeleteConfirmId(b.id)} className="AdminBtnReject" disabled={formSaving}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const DEFAULT_PAGE_SIZE = 20;

const OrdersSection: React.FC = () => {
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [createUserId, setCreateUserId] = useState('');
  const [createMarketId, setCreateMarketId] = useState('');
  const [createSide, setCreateSide] = useState<'YES' | 'NO'>('YES');
  const [createAmount, setCreateAmount] = useState('');
  const [createCategoryId, setCreateCategoryId] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [markets, setMarkets] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);

  const load = useCallback((p: number, s: string) => {
    setLoading(true);
    setError(null);
    fetchAdminOrders({ page: p, limit: DEFAULT_PAGE_SIZE, search: s || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load orders'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, search);
  }, [page, search, load]);

  useEffect(() => {
    fetchOrderCategories({ limit: 100 }).then((r: any) => setCategories(r.data || []));
  }, []);
  useEffect(() => {
    if (showCreate) {
      fetchAdminMarkets({ limit: 100 }).then((r: any) => setMarkets(r.data || []));
      fetchAdminUsers({ limit: 100 }).then((r: any) => setUsers(r.data || []));
    }
  }, [showCreate]);

  const handleCancel = async (orderId: number) => {
    setActingId(orderId);
    try {
      await apiCancelOrder(orderId);
      load(page, search);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to cancel');
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = async (orderId: number) => {
    setActingId(orderId);
    try {
      await apiDeleteOrder(orderId);
      setDeleteConfirmId(null);
      load(page, search);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete');
    } finally {
      setActingId(null);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const userId = parseInt(createUserId, 10);
    const marketId = parseInt(createMarketId, 10);
    const categoryId = createCategoryId ? parseInt(createCategoryId, 10) : undefined;
    if (!createUserId || !createMarketId || !createAmount || isNaN(userId) || isNaN(marketId)) {
      setCreateError('Fill user, market and amount');
      return;
    }
    setCreateSubmitting(true);
    setCreateError(null);
    try {
      await apiCreateOrder({ userId, marketId, side: createSide, amount: createAmount, categoryId });
      setShowCreate(false);
      setCreateUserId('');
      setCreateMarketId('');
      setCreateAmount('');
      setCreateCategoryId('');
      setPage(1);
      load(1, search);
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || err?.message || 'Failed to create order');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;
  const orders = result?.data ?? [];

  return (
    <section>
      <h2>Orders</h2>
      <p>YES/NO orders. Create, cancel or delete OPEN orders.</p>
      <div className="AdminToolbar">
        <input
          type="text"
          placeholder="Search by id, user, market, status"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
          className="LoginInput"
          style={{ width: 260, marginRight: 8 }}
        />
        <button type="button" onClick={() => setSearch(searchInput)}>Search</button>
        <button type="button" onClick={() => setShowCreate(true)} className="AdminBtnApprove" style={{ marginLeft: 8 }}>Create order</button>
      </div>
      {showCreate && (
        <form onSubmit={handleCreate} className="ResolveForm" style={{ marginTop: 16, marginBottom: 16 }}>
          <h3>Create order</h3>
          <label>User: <select value={createUserId} onChange={(e) => setCreateUserId(e.target.value)} required>
            <option value="">—</option>
            {users.map((u) => <option key={u.id} value={u.id}>#{u.id} {u.username || u.telegramId || u.adminEmail || ''}</option>)}
          </select></label>
          <label>Market: <select value={createMarketId} onChange={(e) => setCreateMarketId(e.target.value)} required>
            <option value="">—</option>
            {markets.filter((m: any) => m.status === 'OPEN').map((m: any) => <option key={m.id} value={m.id}>#{m.id} {m.question?.slice(0, 50)}</option>)}
          </select></label>
          <label>Category: <select value={createCategoryId} onChange={(e) => setCreateCategoryId(e.target.value)}>
            <option value="">—</option>
            {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
          </select></label>
          <label>Side: <select value={createSide} onChange={(e) => setCreateSide(e.target.value as 'YES' | 'NO')}>
            <option value="YES">YES</option>
            <option value="NO">NO</option>
          </select></label>
          <label>Amount: <input type="text" value={createAmount} onChange={(e) => setCreateAmount(e.target.value)} placeholder="e.g. 10" required /></label>
          {createError && <p className="AdminError">{createError}</p>}
          <button type="submit" disabled={createSubmitting}>{createSubmitting ? 'Creating...' : 'Create'}</button>
          <button type="button" onClick={() => setShowCreate(false)} style={{ marginLeft: 8 }}>Cancel</button>
        </form>
      )}
      <div className="AdminTableWrap">
        <table className="AdminTable">
          <thead>
            <tr>
              <th>#</th>
              <th>Username</th>
              <th>Market title</th>
              <th>side</th>
              <th>price</th>
              <th>amount</th>
              <th>status</th>
              <th>created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td>{o.id}</td>
                <td title={(o.user?.id ?? o.userId) != null ? `user #${o.user?.id ?? o.userId}` : undefined}>{displayAdminUsername(o.user, o.userId)}</td>
                <td title={o.market?.id != null ? `market #${o.market.id}` : undefined}>{o.market?.question ?? '—'}</td>
                <td>{o.side}</td>
                <td>{o.price}</td>
                <td>{o.amount}</td>
                <td>{o.status}</td>
                <td>{o.createdAt ? new Date(o.createdAt).toLocaleString() : '—'}</td>
                <td>
                  {o.status === 'OPEN' && (
                    <>
                      <button type="button" onClick={() => handleCancel(o.id)} disabled={!!actingId}>{actingId === o.id ? '...' : 'Cancel'}</button>
                      {deleteConfirmId === o.id ? (
                        <>
                          <span style={{ marginLeft: 4 }}>Confirm delete?</span>
                          <button type="button" onClick={() => handleDelete(o.id)} disabled={!!actingId} className="AdminBtnReject" style={{ marginLeft: 4 }}>Yes</button>
                          <button type="button" onClick={() => setDeleteConfirmId(null)} style={{ marginLeft: 4 }}>No</button>
                        </>
                      ) : (
                        <button type="button" onClick={() => setDeleteConfirmId(o.id)} className="AdminBtnReject" style={{ marginLeft: 4 }}>Delete</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const PositionsSection: React.FC = () => {
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchAdminPositions({ page, limit: 20, search: search || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load positions'))
      .finally(() => setLoading(false));
  }, [page, search]);

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const positions = result?.data ?? [];

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;
  return (
    <section>
      <h2>Positions</h2>
      <div className="AdminToolbar">
        <input type="text" placeholder="Search by user or market id" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setSearch(searchInput), setPage(1))} className="LoginInput" style={{ width: 220, marginRight: 8 }} />
        <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
      </div>
      <div className="AdminTableWrap">
        <table className="AdminTable">
          <thead>
            <tr>
              <th>#</th>
              <th>Username</th>
              <th>Market title</th>
              <th>side</th>
              <th>stake</th>
              <th>payout_total</th>
              <th>payout_realized</th>
              <th>status</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td title={(p.user?.id ?? p.userId) != null ? `user #${p.user?.id ?? p.userId}` : undefined}>{displayAdminUsername(p.user, p.userId)}</td>
                <td title={p.market?.id != null ? `market #${p.market.id}` : undefined}>{p.market?.question ?? '—'}</td>
                <td>{p.side}</td>
                <td>{p.stakeTotal}</td>
                <td>{p.payoutTotal}</td>
                <td>{p.payoutRealized}</td>
                <td>{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const WithdrawalsSection: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [usdtList, setUsdtList] = useState<any[] | null>(null);
  const [usdtPage, setUsdtPage] = useState(1);
  const [usdtTotal, setUsdtTotal] = useState(0);
  const [usdtStatusFilter, setUsdtStatusFilter] = useState<string>('');
  const [usdtLoading, setUsdtLoading] = useState(false);
  const [usdtActionId, setUsdtActionId] = useState<number | null>(null);
  const [usdtError, setUsdtError] = useState<string | null>(null);

  const loadUsdt = useCallback(() => {
    setUsdtLoading(true);
    setUsdtError(null);
    fetchAdminUsdtWithdrawals({
      page: usdtPage,
      limit: 20,
      status: usdtStatusFilter || undefined,
    })
      .then((res) => {
        setUsdtList(res.data ?? []);
        setUsdtTotal(res.total ?? 0);
      })
      .catch((e: any) => setUsdtError(e?.response?.data?.message || e?.message || 'Failed to load'))
      .finally(() => setUsdtLoading(false));
  }, [usdtPage, usdtStatusFilter]);

  useEffect(() => {
    loadUsdt();
  }, [loadUsdt]);

  useEffect(() => {
    fetchAdminUsers({ limit: 500 })
      .then((res: any) => setUsers(res?.data ?? []))
      .catch(() => {});
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const uid = userId.trim();
    const amt = amount.trim();
    if (!uid || !amt) {
      setError('Select user and enter amount');
      return;
    }
    const num = parseFloat(amt);
    if (!Number.isFinite(num) || num <= 0) {
      setError('Amount must be a positive number');
      return;
    }
    setError(null);
    setSuccess(false);
    setLoading(true);
    try {
      await apiRecordWithdrawal({
        userId: Number(uid),
        amount: amt,
        comment: comment.trim() || undefined,
      });
      setSuccess(true);
      setAmount('');
      setComment('');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const usdtTotalPages = Math.max(1, Math.ceil(usdtTotal / 20));

  return (
    <section>
      <h2>Record withdrawal</h2>
      <p>Record that a certain amount was withdrawn for a user. Balance will be debited and a WITHDRAWAL transaction will be created.</p>
      <form onSubmit={onSubmit} className="AdminForm AdminForm--narrow">
        <label>User * <select value={userId} onChange={(e) => setUserId(e.target.value)} required className="LoginInput" style={{ width: '100%' }}>
          <option value="">— Select user —</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>#{u.id} {u.username || u.telegramId || u.adminEmail || '—'}</option>
          ))}
        </select></label>
        <label>Amount * <input type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required className="LoginInput" style={{ width: '100%' }} /></label>
        <label>Comment <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional" className="LoginInput" style={{ width: '100%' }} /></label>
        {error && <p className="AdminError">{error}</p>}
        {success && <p className="AdminSuccess">Withdrawal recorded.</p>}
        <button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Record withdrawal'}</button>
      </form>

      <hr style={{ margin: '32px 0', borderColor: '#333' }} />

      <h2>USDT jetton withdrawals (TON)</h2>
      <p>User requests from the mini-app. Approve sends USDT jetton (mock/real per backend). Reject refunds TON balance.</p>
      <div style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label>
          Status{' '}
          <select
            value={usdtStatusFilter}
            onChange={(e) => { setUsdtPage(1); setUsdtStatusFilter(e.target.value); }}
            className="LoginInput"
          >
            <option value="">All</option>
            <option value="PENDING">PENDING</option>
            <option value="SENT">SENT</option>
            <option value="REJECTED">REJECTED</option>
            <option value="FAILED">FAILED</option>
          </select>
        </label>
        <button type="button" onClick={() => loadUsdt()} disabled={usdtLoading}>Refresh</button>
      </div>
      {usdtError && <p className="AdminError">{usdtError}</p>}
      {usdtLoading && !usdtList?.length ? <p>Loading…</p> : null}
      {usdtList && (
        <div className="AdminTableWrap">
          <table className="AdminTable">
            <thead>
              <tr>
                <th>id</th>
                <th>user</th>
                <th>amount TON</th>
                <th>destination</th>
                <th>status</th>
                <th>tx</th>
                <th>created</th>
                <th>actions</th>
              </tr>
            </thead>
            <tbody>
              {usdtList.map((r: any) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>#{r.user?.id} {r.user?.username || r.user?.telegramId || '—'}</td>
                  <td>{r.amountTon}</td>
                  <td style={{ maxWidth: 180, wordBreak: 'break-all', fontSize: 12 }}>{r.destinationAddress}</td>
                  <td>{r.status}</td>
                  <td style={{ maxWidth: 120, wordBreak: 'break-all', fontSize: 11 }}>{r.txHash || '—'}</td>
                  <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                  <td>
                    {r.status === 'PENDING' ? (
                      <>
                        <button
                          type="button"
                          className="AdminBtnApprove"
                          disabled={usdtActionId !== null}
                          onClick={async () => {
                            setUsdtActionId(r.id);
                            setUsdtError(null);
                            try {
                              await apiApproveUsdtWithdrawal(r.id);
                              loadUsdt();
                            } catch (e: any) {
                              setUsdtError(e?.response?.data?.message || e?.message || 'Approve failed');
                            } finally {
                              setUsdtActionId(null);
                            }
                          }}
                        >Approve</button>
                        <button
                          type="button"
                          className="AdminBtnReject"
                          style={{ marginLeft: 6 }}
                          disabled={usdtActionId !== null}
                          onClick={async () => {
                            setUsdtActionId(r.id);
                            setUsdtError(null);
                            try {
                              await apiRejectUsdtWithdrawal(r.id);
                              loadUsdt();
                            } catch (e: any) {
                              setUsdtError(e?.response?.data?.message || e?.message || 'Reject failed');
                            } finally {
                              setUsdtActionId(null);
                            }
                          }}
                        >Reject</button>
                      </>
                    ) : (
                      <span style={{ color: '#888' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {usdtList && usdtTotalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={usdtPage <= 1} onClick={() => setUsdtPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {usdtPage} of {usdtTotalPages} ({usdtTotal} total)</span>
          <button type="button" disabled={usdtPage >= usdtTotalPages} onClick={() => setUsdtPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const ContentSection: React.FC = () => {
  const [subTab, setSubTab] = useState<'categories' | 'items'>('categories');
  const [categories, setCategories] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryForm, setCategoryForm] = useState<{ id?: number; code: string; name: string } | null>(null);
  const [itemForm, setItemForm] = useState<{ id?: number; categoryId: string; title: string; body: string; sortOrder: string } | null>(null);
  const [itemCategoryFilter, setItemCategoryFilter] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'category' | 'item'; id: number } | null>(null);
  const [itemFullscreen, setItemFullscreen] = useState(false);

  const loadCategories = useCallback(() => {
    setLoading(true);
    fetchContentCategories({ limit: 100 })
      .then((r: any) => setCategories(r?.data ?? []))
      .catch(() => setError('Failed to load categories'))
      .finally(() => setLoading(false));
  }, []);

  const loadItems = useCallback((categoryId?: number) => {
    setLoading(true);
    fetchContentItems({ limit: 100, categoryId })
      .then((r: any) => setItems(r?.data ?? []))
      .catch(() => setError('Failed to load items'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (subTab === 'items') loadItems(itemCategoryFilter ? parseInt(itemCategoryFilter, 10) : undefined);
  }, [subTab, itemCategoryFilter, loadItems]);

  const saveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryForm) return;
    if (!categoryForm.code.trim() || !categoryForm.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (categoryForm.id) {
        await apiUpdateContentCategory(categoryForm.id, { code: categoryForm.code.trim(), name: categoryForm.name.trim() });
      } else {
        await apiCreateContentCategory({ code: categoryForm.code.trim(), name: categoryForm.name.trim() });
      }
      setCategoryForm(null);
      loadCategories();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemForm) return;
    if (!itemForm.title.trim()) return;
    const categoryId = itemForm.categoryId ? parseInt(itemForm.categoryId, 10) : undefined;
    if (!categoryId) {
      setError('Select category');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { title: itemForm.title.trim(), body: itemForm.body.trim() || null, sortOrder: itemForm.sortOrder ? parseInt(itemForm.sortOrder, 10) : 0 };
      if (itemForm.id) {
        await apiUpdateContentItem(itemForm.id, payload);
      } else {
        await apiCreateContentItem({ categoryId, ...payload });
      }
      setItemForm(null);
      loadItems(itemCategoryFilter ? parseInt(itemCategoryFilter, 10) : undefined);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setError(null);
    try {
      if (deleteConfirm.type === 'category') await apiDeleteContentCategory(deleteConfirm.id);
      else await apiDeleteContentItem(deleteConfirm.id);
      setDeleteConfirm(null);
      loadCategories();
      loadItems(itemCategoryFilter ? parseInt(itemCategoryFilter, 10) : undefined);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <h2>Content (FAQ, Tutorials)</h2>
      <p>Manage categories (e.g. faq, tutorials) and content items. Public API: GET /content/categories, GET /content/categories/:code/items</p>
      <div className="AdminTabs" style={{ marginBottom: 12 }}>
        <button type="button" className={subTab === 'categories' ? 'AdminTab active' : 'AdminTab'} onClick={() => setSubTab('categories')}>Categories</button>
        <button type="button" className={subTab === 'items' ? 'AdminTab active' : 'AdminTab'} onClick={() => setSubTab('items')}>Items</button>
      </div>
      {error && <p className="AdminError">{error}</p>}

      {subTab === 'categories' && (
        <>
          <button type="button" onClick={() => setCategoryForm({ code: '', name: '' })}>Add category</button>
          {categoryForm && (
            <form onSubmit={saveCategory} className="AdminForm AdminForm--narrow" style={{ marginTop: 12 }}>
              <label>Code <input value={categoryForm.code} onChange={(e) => setCategoryForm((f) => f ? { ...f, code: e.target.value } : null)} placeholder="faq" required className="LoginInput" /></label>
              <label>Name <input value={categoryForm.name} onChange={(e) => setCategoryForm((f) => f ? { ...f, name: e.target.value } : null)} placeholder="FAQ" required className="LoginInput" /></label>
              <div className="AdminFormActions">
                <button type="submit" disabled={saving}>Save</button>
                <button type="button" onClick={() => setCategoryForm(null)}>Cancel</button>
              </div>
            </form>
          )}
          <div className="AdminTableWrap" style={{ marginTop: 12 }}>
            <table className="AdminTable">
              <thead><tr><th>#</th><th>code</th><th>name</th><th>Actions</th></tr></thead>
              <tbody>
                {loading && categories.length === 0 ? <tr><td colSpan={4}>Loading...</td></tr> : categories.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>{c.code}</td>
                    <td>{c.name}</td>
                    <td>
                      <button type="button" onClick={() => setCategoryForm({ id: c.id, code: c.code, name: c.name })}>Edit</button>
                      {' '}
                      {deleteConfirm?.type === 'category' && deleteConfirm?.id === c.id ? (
                        <><button type="button" className="AdminBtnReject" onClick={doDelete}>Confirm delete</button> <button type="button" onClick={() => setDeleteConfirm(null)}>Cancel</button></>
                      ) : (
                        <button type="button" className="AdminBtnReject" onClick={() => setDeleteConfirm({ type: 'category', id: c.id })}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {subTab === 'items' && (
        <>
          <div className="AdminToolbar" style={{ marginBottom: 8 }}>
            <label>Category: <select value={itemCategoryFilter} onChange={(e) => setItemCategoryFilter(e.target.value)} className="LoginInput">
              <option value="">All</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
            </select></label>
            <button type="button" onClick={() => setItemForm({ categoryId: itemCategoryFilter || (categories[0]?.id?.toString() ?? ''), title: '', body: '', sortOrder: '0' })}>Add item</button>
          </div>
          {categories.length === 0 && <p>Create a category first.</p>}
          {itemForm && (
            <form
              onSubmit={saveItem}
              className={itemFullscreen ? 'AdminForm ContentItemForm fullscreen' : 'AdminForm ContentItemForm'}
              style={{ marginTop: 12, maxWidth: itemFullscreen ? 'none' : 760 }}
            >
              <div className="ContentItemFormHeader">
                <h3>{itemForm.id ? `Edit item #${itemForm.id}` : 'Create item'}</h3>
                <button
                  type="button"
                  className="ContentItemFullscreenToggle"
                  onClick={() => setItemFullscreen((v) => !v)}
                >
                  {itemFullscreen ? 'Exit full screen' : 'Full screen'}
                </button>
              </div>
              <label>Category * <select value={itemForm.categoryId} onChange={(e) => setItemForm((f) => f ? { ...f, categoryId: e.target.value } : null)} required className="LoginInput">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
              </select></label>
              <label>Title * <input value={itemForm.title} onChange={(e) => setItemForm((f) => f ? { ...f, title: e.target.value } : null)} className="LoginInput" style={{ width: '100%' }} /></label>
              <div className="MarkdownEditorField" data-color-mode="light">
                <span>Body (Markdown)</span>
                <MDEditor
                  value={itemForm.body}
                  onChange={(val) =>
                    setItemForm((f) => (f ? { ...f, body: val ?? '' } : null))
                  }
                  height={itemFullscreen ? 520 : 320}
                  preview="live"
                  hideToolbar={false}
                />
              </div>
              <label>Sort order <input type="number" value={itemForm.sortOrder} onChange={(e) => setItemForm((f) => f ? { ...f, sortOrder: e.target.value } : null)} className="LoginInput" /></label>
              <div className="ContentItemFormActions">
                <button type="submit" disabled={saving}>Save</button>
                <button type="button" onClick={() => { setItemForm(null); setItemFullscreen(false); }}>Cancel</button>
              </div>
            </form>
          )}
          <div className="AdminTableWrap" style={{ marginTop: 12 }}>
            <table className="AdminTable">
              <thead><tr><th>#</th><th>Category</th><th>Title</th><th>Sort</th><th>Actions</th></tr></thead>
              <tbody>
                {loading && items.length === 0 ? <tr><td colSpan={5}>Loading...</td></tr> : items.map((i) => (
                  <tr key={i.id}>
                    <td>{i.id}</td>
                    <td>{i.category?.code ?? i.categoryId}</td>
                    <td>{(i.title || '').slice(0, 50)}{(i.title || '').length > 50 ? '…' : ''}</td>
                    <td>{i.sortOrder ?? 0}</td>
                    <td>
                      <button type="button" onClick={() => setItemForm({ id: i.id, categoryId: String(i.category?.id ?? i.categoryId), title: i.title ?? '', body: i.body ?? '', sortOrder: String(i.sortOrder ?? 0) })}>Edit</button>
                      {' '}
                      {deleteConfirm?.type === 'item' && deleteConfirm?.id === i.id ? (
                        <><button type="button" className="AdminBtnReject" onClick={doDelete}>Confirm delete</button> <button type="button" onClick={() => setDeleteConfirm(null)}>Cancel</button></>
                      ) : (
                        <button type="button" className="AdminBtnReject" onClick={() => setDeleteConfirm({ type: 'item', id: i.id })}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

const ADMIN_TRANSACTION_TYPES = [
  'DEPOSIT',
  'WITHDRAWAL',
  'BET_LOCK',
  'BET_RELEASE',
  'PAYOUT',
  'REVSHARE_CREDIT',
  'REFERRAL_BONUS',
] as const;

function adminLogsUserLabel(
  u: { id?: number; username?: string | null } | undefined | null,
  fallbackUserId?: number | null,
): string {
  const name = u?.username?.trim();
  if (name) return `@${name}`;
  const id = u?.id ?? fallbackUserId;
  if (id != null) return `— (#${id})`;
  return '—';
}

function adminTxTypeClassName(type: string): string {
  const slug = type.toLowerCase().replace(/_/g, '-');
  return `AdminTxType AdminTxType--${slug}`;
}

const LogsSection: React.FC = () => {
  const [subTab, setSubTab] = useState<'resolution' | 'limits' | 'transactions'>('resolution');
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [txFilters, setTxFilters] = useState({ search: '', type: '', user: '', amount: '' });
  const [txInputs, setTxInputs] = useState({ search: '', type: '', user: '', amount: '' });

  useEffect(() => {
    setLoading(true);
    setError(null);
    const common = { page, limit: 20 };
    const p =
      subTab === 'resolution'
        ? fetchResolutionLogs({ ...common, search: search || undefined })
        : subTab === 'limits'
          ? fetchUserLimits({ ...common, search: search || undefined })
          : fetchTransactions({
              ...common,
              search: txFilters.search || undefined,
              amount: txFilters.amount.trim() || undefined,
              type: txFilters.type || undefined,
              user: txFilters.user.trim() || undefined,
            });
    p.then((res: any) => setResult(res))
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  }, [subTab, page, search, txFilters]);

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const data = result?.data ?? [];

  const applyTxFilters = () => {
    setTxFilters({ ...txInputs });
    setPage(1);
  };

  return (
    <section>
      <h2>Logs, limits, resolution history (audit)</h2>
      <div className="AdminTabs">
        <button type="button" className={subTab === 'resolution' ? 'AdminTab active' : 'AdminTab'} onClick={() => { setSubTab('resolution'); setPage(1); }}>Resolution history</button>
        <button type="button" className={subTab === 'limits' ? 'AdminTab active' : 'AdminTab'} onClick={() => { setSubTab('limits'); setPage(1); }}>Limits</button>
        <button type="button" className={subTab === 'transactions' ? 'AdminTab active' : 'AdminTab'} onClick={() => { setSubTab('transactions'); setPage(1); }}>Transactions</button>
      </div>
      {subTab === 'transactions' ? (
        <div className="AdminToolbar AdminToolbar--logsTx" style={{ marginTop: 8 }}>
          <select
            value={txInputs.type}
            onChange={(e) => setTxInputs((s) => ({ ...s, type: e.target.value }))}
            className="LoginInput AdminSelect"
            aria-label="Transaction type"
          >
            <option value="">All types</option>
            {ADMIN_TRANSACTION_TYPES.map((t) => (
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="User (@name or id)"
            value={txInputs.user}
            onChange={(e) => setTxInputs((s) => ({ ...s, user: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && applyTxFilters()}
            className="LoginInput"
            style={{ width: 160 }}
          />
          <input
            type="text"
            placeholder="Amount (exact)"
            value={txInputs.amount}
            onChange={(e) => setTxInputs((s) => ({ ...s, amount: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && applyTxFilters()}
            className="LoginInput"
            style={{ width: 120 }}
          />
          <input
            type="text"
            placeholder="Tx or user id"
            value={txInputs.search}
            onChange={(e) => setTxInputs((s) => ({ ...s, search: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && applyTxFilters()}
            className="LoginInput"
            style={{ width: 140 }}
          />
          <button type="button" onClick={applyTxFilters}>Search</button>
        </div>
      ) : (
        <div className="AdminToolbar" style={{ marginTop: 8 }}>
          <input
            type="text"
            placeholder={subTab === 'limits' ? 'User id or username' : 'Search by id'}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (setSearch(searchInput), setPage(1))}
            className="LoginInput"
            style={{ width: 220, marginRight: 8 }}
          />
          <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
        </div>
      )}
      {loading && !result && <p>Loading...</p>}
      {error && !result && <p className="AdminError">{error}</p>}
      {!loading && !error && subTab === 'resolution' && (
        <>
          <div className="AdminTableWrap">
            <table className="AdminTable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>market</th>
                  <th>resolvedBy</th>
                  <th>resolution</th>
                  <th>comment</th>
                  <th>created</th>
                </tr>
              </thead>
              <tbody>
                {data.map((r: any) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.market?.id} {r.market?.question?.slice(0, 40)}</td>
                    <td>{adminLogsUserLabel(r.resolvedBy)}</td>
                    <td>{r.resolution}</td>
                    <td>{r.comment ?? '—'}</td>
                    <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result && totalPages > 1 && (
            <div className="AdminPagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
      {!loading && !error && subTab === 'limits' && (
        <>
          <div className="AdminTableWrap">
            <table className="AdminTable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>user</th>
                  <th>max_deposit_daily</th>
                  <th>max_bet_single</th>
                  <th>kyc_level</th>
                  <th>kyc_status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((l: any) => (
                  <tr key={l.id}>
                    <td>{l.id}</td>
                    <td>{adminLogsUserLabel(l.user, l.userId)}</td>
                    <td>{l.maxDepositDaily ?? '—'}</td>
                    <td>{l.maxBetSingle ?? '—'}</td>
                    <td>{l.kycLevel ?? '—'}</td>
                    <td>{l.kycStatus ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result && totalPages > 1 && (
            <div className="AdminPagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
      {!loading && !error && subTab === 'transactions' && (
        <>
          <div className="AdminTableWrap">
            <table className="AdminTable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>user</th>
                  <th>type</th>
                  <th>amount</th>
                  <th>balance_before</th>
                  <th>balance_after</th>
                  <th>created</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t: any) => (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>{adminLogsUserLabel(t.user, t.userId)}</td>
                    <td>
                      <span className={adminTxTypeClassName(String(t.type))}>{String(t.type).replace(/_/g, ' ')}</span>
                    </td>
                    <td>{t.amount}</td>
                    <td>{t.balanceBefore}</td>
                    <td>{t.balanceAfter}</td>
                    <td>{t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result && totalPages > 1 && (
            <div className="AdminPagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

const NotificationsSection: React.FC = () => {
  const notificationTypeOptions = [
    { value: '', label: 'None' },
    { value: 'general', label: 'General' },
    { value: 'market_alert', label: 'Market alert' },
    { value: 'deposit', label: 'Deposit' },
    { value: 'payout', label: 'Payout' },
    { value: 'referral', label: 'Referral' },
    { value: 'security', label: 'Security' },
  ];
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState('');
  const [notifyTelegram, setNotifyTelegram] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const load = useCallback((p: number, s: string) => {
    setLoading(true);
    setError(null);
    fetchAdminNotifications({ page: p, limit: 20, search: s || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load notifications'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, search);
  }, [page, search, load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const created = await apiCreateNotification({
        title: title.trim(),
        body: body.trim() || undefined,
        type: type.trim() || undefined,
        notifyTelegram,
      });
      if (created?.telegram?.requested) {
        setError(
          created.telegram.failed > 0
            ? `Created, Telegram: sent ${created.telegram.sent}/${created.telegram.attempted}. Failed: ${created.telegram.failed}`
            : `Created, Telegram: sent ${created.telegram.sent}/${created.telegram.attempted}`,
        );
      } else {
        setError(null);
      }
      setShowCreate(false);
      setTitle('');
      setBody('');
      setType('');
      setNotifyTelegram(true);
      load(page, search);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editId == null || !title.trim()) return;
    setSubmitting(true);
    try {
      await apiUpdateNotification(editId, { title: title.trim(), body: body.trim() || undefined, type: type.trim() || undefined });
      setEditId(null);
      setTitle('');
      setBody('');
      setType('');
      load(page, search);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiDeleteNotification(id);
      setDeleteConfirmId(null);
      load(page, search);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete');
    }
  };

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const data = result?.data ?? [];

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;

  return (
    <section>
      <h2>Notifications</h2>
      <p>Create, edit and delete notifications.</p>
      <div className="AdminToolbar">
        <input type="text" placeholder="Search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setSearch(searchInput), setPage(1))} className="LoginInput" style={{ width: 200, marginRight: 8 }} />
        <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
        <button type="button" onClick={() => { setShowCreate(true); setEditId(null); setTitle(''); setBody(''); setType(''); setNotifyTelegram(true); }} className="AdminBtnApprove" style={{ marginLeft: 8 }}>Create</button>
      </div>
      {error && <p className="AdminError">{error}</p>}
      {showCreate && (
        <form onSubmit={handleCreate} className="ResolveForm" style={{ marginTop: 16, marginBottom: 16 }}>
          <h3>Create notification</h3>
          <label>Title: <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required /></label>
          <label>Body: <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} /></label>
          <label>
            Type:
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {notificationTypeOptions.map((opt) => (
                <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={notifyTelegram}
              onChange={(e) => setNotifyTelegram(e.target.checked)}
            />
            Send to Telegram bot users
          </label>
          <button type="submit" disabled={submitting}>{submitting ? 'Creating...' : 'Create'}</button>
          <button type="button" onClick={() => setShowCreate(false)} style={{ marginLeft: 8 }}>Cancel</button>
        </form>
      )}
      {editId != null && (
        <form onSubmit={handleUpdate} className="ResolveForm" style={{ marginTop: 16, marginBottom: 16 }}>
          <h3>Edit notification</h3>
          <label>Title: <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required /></label>
          <label>Body: <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} /></label>
          <label>
            Type:
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {notificationTypeOptions.map((opt) => (
                <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
          <button type="button" onClick={() => { setEditId(null); setTitle(''); setBody(''); setType(''); }} style={{ marginLeft: 8 }}>Cancel</button>
        </form>
      )}
      <div className="AdminTableWrap">
        <table className="AdminTable">
          <thead>
            <tr><th>#</th><th>title</th><th>body</th><th>type</th><th>created</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {data.map((n: any) => (
              <tr key={n.id}>
                <td>{n.id}</td>
                <td>{n.title}</td>
                <td>{(n.body || '').slice(0, 60)}{(n.body && n.body.length > 60) ? '…' : ''}</td>
                <td>{n.type ?? '—'}</td>
                <td>{n.createdAt ? new Date(n.createdAt).toLocaleString() : '—'}</td>
                <td>
                  <button type="button" onClick={() => { setEditId(n.id); setTitle(n.title); setBody(n.body || ''); setType(n.type || ''); setShowCreate(false); }}>Edit</button>
                  {deleteConfirmId === n.id ? (
                    <>
                      <span style={{ marginLeft: 4 }}>Delete?</span>
                      <button type="button" onClick={() => handleDelete(n.id)} className="AdminBtnReject" style={{ marginLeft: 4 }}>Yes</button>
                      <button type="button" onClick={() => setDeleteConfirmId(null)} style={{ marginLeft: 4 }}>No</button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setDeleteConfirmId(n.id)} className="AdminBtnReject" style={{ marginLeft: 4 }}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

const OrderCategoriesSection: React.FC = () => {
  const [result, setResult] = useState<{ data: any[]; total: number; page: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const load = useCallback((p: number, s: string) => {
    setLoading(true);
    setError(null);
    fetchOrderCategories({ page: p, limit: 20, search: s || undefined })
      .then((res: any) => setResult(res))
      .catch(() => setError('Failed to load order categories'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, search);
  }, [page, search, load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) return;
    setSubmitting(true);
    try {
      await apiCreateOrderCategory({ name: name.trim(), code: code.trim(), description: description.trim() || undefined });
      setShowCreate(false);
      setName('');
      setCode('');
      setDescription('');
      load(page, search);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editId == null || !name.trim() || !code.trim()) return;
    setSubmitting(true);
    try {
      await updateOrderCategoryApi(editId, { name: name.trim(), code: code.trim(), description: description.trim() || undefined });
      setEditId(null);
      setName('');
      setCode('');
      setDescription('');
      load(page, search);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiDeleteOrderCategory(id);
      setDeleteConfirmId(null);
      load(page, search);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete');
    }
  };

  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const data = result?.data ?? [];

  if (loading && !result) return <p>Loading...</p>;
  if (error && !result) return <p className="AdminError">{error}</p>;

  return (
    <section>
      <h2>Order categories</h2>
      <p>Categories for orders. Assign in Orders when creating or editing.</p>
      <div className="AdminToolbar">
        <input type="text" placeholder="Search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setSearch(searchInput), setPage(1))} className="LoginInput" style={{ width: 200, marginRight: 8 }} />
        <button type="button" onClick={() => { setSearch(searchInput); setPage(1); }}>Search</button>
        <button type="button" onClick={() => { setShowCreate(true); setEditId(null); setName(''); setCode(''); setDescription(''); }} className="AdminBtnApprove" style={{ marginLeft: 8 }}>Create</button>
      </div>
      {showCreate && (
        <form onSubmit={handleCreate} className="ResolveForm" style={{ marginTop: 16, marginBottom: 16 }}>
          <h3>Create category</h3>
          <label>Name: <input type="text" value={name} onChange={(e) => setName(e.target.value)} required /></label>
          <label>Code: <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="unique code" required /></label>
          <label>Description: <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
          <button type="submit" disabled={submitting}>{submitting ? 'Creating...' : 'Create'}</button>
          <button type="button" onClick={() => setShowCreate(false)} style={{ marginLeft: 8 }}>Cancel</button>
        </form>
      )}
      {editId != null && (
        <form onSubmit={handleUpdate} className="ResolveForm" style={{ marginTop: 16, marginBottom: 16 }}>
          <h3>Edit category</h3>
          <label>Name: <input type="text" value={name} onChange={(e) => setName(e.target.value)} required /></label>
          <label>Code: <input type="text" value={code} onChange={(e) => setCode(e.target.value)} required /></label>
          <label>Description: <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
          <button type="submit" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
          <button type="button" onClick={() => { setEditId(null); setName(''); setCode(''); setDescription(''); }} style={{ marginLeft: 8 }}>Cancel</button>
        </form>
      )}
      <div className="AdminTableWrap">
        <table className="AdminTable">
          <thead>
            <tr><th>#</th><th>name</th><th>code</th><th>description</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {data.map((c: any) => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td>{c.name}</td>
                <td>{c.code}</td>
                <td>{(c.description || '').slice(0, 40)}{(c.description && c.description.length > 40) ? '…' : ''}</td>
                <td>
                  <button type="button" onClick={() => { setEditId(c.id); setName(c.name); setCode(c.code); setDescription(c.description || ''); setShowCreate(false); }}>Edit</button>
                  {deleteConfirmId === c.id ? (
                    <>
                      <span style={{ marginLeft: 4 }}>Delete?</span>
                      <button type="button" onClick={() => handleDelete(c.id)} className="AdminBtnReject" style={{ marginLeft: 4 }}>Yes</button>
                      <button type="button" onClick={() => setDeleteConfirmId(null)} style={{ marginLeft: 4 }}>No</button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setDeleteConfirmId(c.id)} className="AdminBtnReject" style={{ marginLeft: 4 }}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result && totalPages > 1 && (
        <div className="AdminPagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span style={{ margin: '0 12px' }}>Page {page} of {totalPages} ({result.total} total)</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
};

export default App;
